import { RoomEntryState } from '../enums/roomEntryState';
import { hubConnection } from './provider';
import { SignalRState } from './state';

export const subscribeToEvents = (setValue: Function, logout: Function) => {
    hubConnection.on('uiConnected', (
        connectedSites: {
            siteId: number;
            roomState: { state: RoomEntryState, userName: string };
            isConnected: boolean;
        }[],
    ) => {
        const serviceStatus = new Map<number, boolean>();
        const roomState = new Map<number, { roomEntryState: RoomEntryState; customerName: string }>();

        connectedSites.forEach((site) => {
            serviceStatus.set(site.siteId, site.isConnected);
            roomState.set(site.siteId, {
                roomEntryState: site.roomState.state,
                customerName: site.roomState.userName,
            });
        });

        setValue((state: SignalRState): SignalRState => {
            return {
                ...state,
                serviceStatus,
                roomState,
                uiConnected: true,
            };
        });
    });

    hubConnection.on('serviceConnectionStatusChanged', (siteId: number, online: boolean) => {
        setValue((state: SignalRState): SignalRState => {
            return { ...state, serviceStatus: state.serviceStatus.set(siteId, online) };
        });
    });

    hubConnection.on("alarmStateChanged", (
        siteId: number,
        alarmActive: boolean,
    ) => {
        setValue((state: SignalRState): SignalRState => {
            return {
                ...state,
                alarmStatus: state.alarmStatus.set(siteId, alarmActive),
            };
        });
    });

    hubConnection.on("roomStateChanged", (
        siteId: number,
        roomEntryState: RoomEntryState,
        customerName: string,
    ) => {
        setValue((state: SignalRState): SignalRState => {
            return {
                ...state,
                roomState: state.roomState.set(siteId, { roomEntryState, customerName }),
            };
        });
    });

    hubConnection.on('userSessionChanged', (userName: string, sessionId: string) => {
        try {
            const currentSessionId = localStorage.getItem('sessionId');
            console.debug(
                'userSessionChanged:' + sessionId + ' Current Session Id:' + currentSessionId
            );

            if (sessionId !== currentSessionId) {
                console.debug('logout:' + sessionId);
                logout();
            }
        } catch (error) {
            console.debug(error);
        }
    });

    hubConnection.on('sensorResetCompleted', (siteId: number) => {
        setValue((state: SignalRState): SignalRState => {
            return { ...state, sensorResetCompleted: siteId };
        });
    });

    hubConnection.on('SystemRestartTriggered', (siteId: number) => {
        setValue((state: SignalRState): SignalRState => {
            return { ...state, systemRestartTriggered: siteId };
        });
    });

    // The service acks before it stops, so this only ever reports "about to stop" — there is
    // no failure mode to report, hence success is always true.
    hubConnection.on('ServiceRestartTriggered', (siteId: number) => {
        setValue((state: SignalRState): SignalRState => {
            return { ...state, serviceRestartTriggered: { siteId, success: true } };
        });
    });

    hubConnection.on('RestartHmiDeviceCompleted', (siteId: number, success: boolean) => {
        setValue((state: SignalRState): SignalRState => {
            return { ...state, restartHmiDeviceCompleted: { siteId, success } };
        });
    });

    hubConnection.on('RestartBiometricDeviceCompleted', (siteId: number, success: boolean) => {
        setValue((state: SignalRState): SignalRState => {
            return { ...state, restartBiometricDeviceCompleted: { siteId, success } };
        });
    });

    hubConnection.on('ChangePasswordCompleted', (siteId: number, success: boolean) => {
        setValue((state: SignalRState): SignalRState => {
            return { ...state, changePasswordCompleted: { siteId, success } };
        });
    });

    hubConnection.on('FingerprintEnrollmentAccepted', (
        userId: number,
        siteId: number,
        code: string,
        requestReference: string,
    ) => {
        setValue((state: SignalRState): SignalRState => {
            return { ...state, enrollmentAcceptedData: { code, requestReference } };
        });
    });

    hubConnection.on('ManualOverrideAccepted', (
        userId: number,
        siteId: number,
        code: string,
        requestReference: string,
    ) => {
        setValue((state: SignalRState): SignalRState => {
            return { ...state, manualOverrideAcceptedData: { code, requestReference } };
        });
    });

    hubConnection.on('MaintenanceModeChanged', (
        siteId: number,
        isEnabled: boolean,
        endTime?: string,
    ) => {
        setValue((state: SignalRState): SignalRState => {
            const maintenanceModeStatus = new Map(state.maintenanceModeStatus);
            if (isEnabled && endTime) {
                maintenanceModeStatus.set(siteId, { isEnabled, endTime: new Date(endTime) });
            } else {
                maintenanceModeStatus.set(siteId, { isEnabled: false });
            }
            return {
                ...state,
                maintenanceModeStatus,
            };
        });
    });
};

export const unsubscribeToEvents = () => {
    hubConnection.off('serviceConnectionStatusChanged');
    hubConnection.off('roomStateChanged');
    hubConnection.off('userSessionChanged');
    hubConnection.off('MaintenanceModeChanged');
};
